import { InputPersonName } from "@/components/Input/PersonName/InputPersonName";
import type {
  PersonNameChildAttribute,
  PersonNameValue,
} from "@/components/Input/PersonName/types";
import { usePersonName } from "@/components/Input/PersonName/usePersonName";
import { VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type Props = CFormItemProps & {
  value?: PersonNameValue;
  onChange: (value: PersonNameValue) => void;
  /** 子属性（各フィールドのname/required/readonlyを個別に指定） */
  childAttributes?: PersonNameChildAttribute[];
};

const DEFAULT_VALUE: PersonNameValue = {
  familyName: "",
  givenName: "",
  familyNameKana: "",
  givenNameKana: "",
};

/**
 * Card-based Form / PersonNameItem
 *
 * 氏名入力用のCFormアイテム。
 * 姓・名・姓カナ・名カナの4フィールドを1つのコンポーネントで管理します。
 *
 * childAttributesプロパティで各フィールドのname/required/readonlyを個別に指定できます。
 * 表示順序は固定で、姓→名→姓カナ→名カナの順序で表示されます。
 */
export const CFormPersonNameItem = buildCFormItem((props: Props) => {
  const value = props.value ?? DEFAULT_VALUE;

  // 表示用のフォーマット済み値を取得
  const { displayName, displayNameKana } = usePersonName({
    value,
    onChange: props.onChange,
    childAttributes: props.childAttributes,
  });

  // 読み取り専用時の表示
  const readComponent = (
    <VStack className="gap-1">
      <Text variant="labelLarge">{displayName}</Text>
      {displayNameKana && (
        <Text variant="pSmall" className="text-neutral-muted-on-fill">
          {displayNameKana}
        </Text>
      )}
    </VStack>
  );

  return (
    <CFormItemBase
      {...props}
      readComponent={readComponent}
      writeComponent={
        <InputPersonName
          value={value}
          onChange={props.onChange}
          childAttributes={props.childAttributes}
          readonly={props.readonly}
        />
      }
    />
  );
});
